/**
 * ClosedLeadControls — reopen, legal hold, purge controls, and rejection notice for closed leads.
 * Extracted from LeadDetailPage.tsx (iteration 4 refactor-split).
 * Updated (landlord-compliance): amber warning badge + Send Rejection Notice button.
 */

import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { badgeVariants } from '../../../components/ui/badge-variants';
import { reopenLead, setLegalHold, purgeLeadData } from '../../../lib/leasing.api';
import type { LeadDetail } from './shared';
import { DECISION_LABELS, DECISION_COLORS } from './shared-constants';
import { RejectionNoticeModal } from './modals/RejectionNoticeModal';

export interface ClosedLeadControlsProps {
  lead: LeadDetail;
  isPlatformAdmin: boolean;
  onUpdated: () => void;
}

export function ClosedLeadControls({ lead, isPlatformAdmin, onUpdated }: ClosedLeadControlsProps) {
  const [isReopening, setIsReopening] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  const [isTogglingHold, setIsTogglingHold] = useState(false);
  const [holdError, setHoldError] = useState<string | null>(null);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [purgeError, setPurgeError] = useState<string | null>(null);
  const [showRejectionNotice, setShowRejectionNotice] = useState(false);

  const canReopen = !lead.dataDisposedAt;

  const isRejected = lead.decision === 'rejected';
  const isSubstantiveBasis = lead.rejectionBasis?.startsWith('substantive_') ?? false;
  const showAdverseActionWarning =
    isRejected &&
    isSubstantiveBasis &&
    lead.adverseActionNoticeRequired === true &&
    lead.rejectionNoticeSentAt === null;

  const handleReopen = async () => {
    setReopenError(null);
    setIsReopening(true);
    try {
      await reopenLead(lead.id);
      onUpdated();
    } catch (err) {
      setReopenError(err instanceof Error ? err.message : 'Failed to reopen lead');
    } finally {
      setIsReopening(false);
    }
  };

  const handleToggleLegalHold = async () => {
    setHoldError(null);
    setIsTogglingHold(true);
    try {
      await setLegalHold(lead.id, { enabled: !lead.legalHold });
      onUpdated();
    } catch (err) {
      setHoldError(err instanceof Error ? err.message : 'Failed to update legal hold');
    } finally {
      setIsTogglingHold(false);
    }
  };

  const handlePurge = async () => {
    setPurgeError(null);
    setIsPurging(true);
    try {
      await purgeLeadData(lead.id);
      setShowPurgeConfirm(false);
      onUpdated();
    } catch (err) {
      setPurgeError(err instanceof Error ? err.message : 'Failed to purge lead data');
    } finally {
      setIsPurging(false);
    }
  };

  return (
    <div data-testid="closed-lead-controls" className="mt-4 space-y-3">
      {/* Decision badge */}
      {lead.decision && (
        <div className="flex flex-wrap items-center gap-2" data-testid="decision-summary">
          <span
            data-testid="decision-badge"
            className={badgeVariants({ variant: DECISION_COLORS[lead.decision] })}
          >
            {DECISION_LABELS[lead.decision]}
          </span>
          {lead.decidedAt && (
            <span className="text-xs text-muted-foreground" data-testid="decided-at">
              Decided {new Date(lead.decidedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          )}
        </div>
      )}

      {/* Decision note */}
      {lead.decisionNote && (
        <p className="text-sm text-muted-foreground italic" data-testid="decision-note">
          "{lead.decisionNote}"
        </p>
      )}

      {/* Adverse action warning badge */}
      {showAdverseActionWarning && (
        <div
          data-testid="adverse-action-warning-badge"
          className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 rounded-md text-sm font-medium bg-warning/10 text-warning border border-warning/30"
          role="status"
        >
          <span>
            <span aria-hidden="true">⚠ </span>
            Notice may be legally required.
          </span>
          <button
            type="button"
            data-testid="adverse-action-send-notice-btn"
            onClick={() => setShowRejectionNotice(true)}
            className="text-xs font-semibold underline underline-offset-2 hover:no-underline transition-all"
          >
            Send notice
          </button>
        </div>
      )}

      {/* Legal hold warning badge */}
      {lead.legalHold && (
        <div
          data-testid="legal-hold-badge"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-warning/10 text-warning border border-warning/30"
          role="status"
        >
          Legal hold active — data will not be disposed
        </div>
      )}

      {/* Data disposed indicator */}
      {lead.dataDisposedAt && (
        <div
          data-testid="data-disposed-badge"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-muted text-muted-foreground"
          role="status"
        >
          Data disposed on {new Date(lead.dataDisposedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        {/* Reopen button */}
        <div className="relative group">
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="lead-reopen-button"
            disabled={!canReopen || isReopening}
            aria-disabled={!canReopen}
            aria-describedby={!canReopen ? 'reopen-disabled-hint' : undefined}
            onClick={() => void handleReopen()}
          >
            {isReopening ? 'Reopening…' : 'Reopen'}
          </Button>
          {!canReopen && (
            <span
              id="reopen-disabled-hint"
              data-testid="reopen-disabled-tooltip"
              className="absolute bottom-full left-0 mb-1 hidden group-hover:block group-focus-within:block w-64 text-xs bg-popover text-popover-foreground border border-border rounded-md px-3 py-1.5 shadow-md z-10"
              role="tooltip"
            >
              Lead data has been disposed — cannot reopen.
            </span>
          )}
        </div>

        {/* Legal hold toggle */}
        <Button
          type="button"
          size="sm"
          variant={lead.legalHold ? 'default' : 'outline'}
          data-testid="lead-legal-hold-toggle"
          disabled={isTogglingHold}
          onClick={() => void handleToggleLegalHold()}
          aria-pressed={lead.legalHold}
        >
          {isTogglingHold
            ? 'Updating…'
            : lead.legalHold
              ? 'Clear Legal Hold'
              : 'Set Legal Hold'}
        </Button>

        {/* Send Rejection Notice button (shown for all closed rejected leads) */}
        {isRejected && (
          <Button
            type="button"
            size="sm"
            variant={lead.rejectionNoticeSentAt ? 'outline' : 'default'}
            data-testid="send-rejection-notice-btn"
            onClick={() => setShowRejectionNotice(true)}
            className={lead.rejectionNoticeSentAt ? 'text-muted-foreground' : ''}
          >
            {lead.rejectionNoticeSentAt ? 'Re-send Rejection Notice' : 'Send Rejection Notice'}
          </Button>
        )}

        {/* Admin-only purge button */}
        {isPlatformAdmin && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="lead-purge-button"
            className="text-destructive border-destructive/40 hover:bg-destructive/10"
            onClick={() => setShowPurgeConfirm(true)}
          >
            Purge Data Now
          </Button>
        )}
      </div>

      {reopenError && (
        <div
          data-testid="reopen-error"
          className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
          role="alert"
        >
          {reopenError}
        </div>
      )}

      {holdError && (
        <div
          data-testid="legal-hold-error"
          className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
          role="alert"
        >
          {holdError}
        </div>
      )}

      {/* Purge confirm dialog */}
      {showPurgeConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="purge-confirm-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="purge-confirm-title"
        >
          <div className="bg-card rounded-lg border border-border w-full max-w-sm p-6 shadow-lg">
            <h3 id="purge-confirm-title" className="text-base font-semibold mb-2 text-destructive">
              Purge Lead Data
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              This will immediately delete all attachments and nullify all PII for this lead.
              This action cannot be undone.
            </p>

            {purgeError && (
              <div
                data-testid="purge-error"
                className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
                role="alert"
              >
                {purgeError}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                data-testid="purge-confirm-submit"
                disabled={isPurging}
                className="flex-1 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => void handlePurge()}
              >
                {isPurging ? 'Purging…' : 'Purge Data'}
              </Button>
              <Button
                type="button"
                variant="outline"
                data-testid="purge-confirm-cancel"
                disabled={isPurging}
                onClick={() => { setShowPurgeConfirm(false); setPurgeError(null); }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Notice Modal */}
      {isRejected && (
        <RejectionNoticeModal
          leadId={lead.id}
          rejectionBasis={lead.rejectionBasis ?? ''}
          isOpen={showRejectionNotice}
          onClose={() => setShowRejectionNotice(false)}
          onNoticeGenerated={onUpdated}
          jurisdictionCoverage={lead.jurisdictionCoverage}
        />
      )}
    </div>
  );
}
